import { useCallback, useEffect, useRef } from "react";
import type { Ref, TextareaHTMLAttributes } from "react";

/**
 * Auto-growing textarea — height tracks `scrollHeight` so the box expands
 * with the content (no inner scroll until `maxHeight` is reached).
 *
 * Behaviour:
 *  - Initial mount: height set from current value's scrollHeight.
 *  - Each input: height resets to 'auto' then snaps to new scrollHeight.
 *  - Optional `maxHeight` (px) caps growth — past that, native scroll kicks in.
 *  - Optional `minRows` (default 2) sets the floor; CSS `min-height` from
 *    line-height ensures the field never collapses below readable size.
 *
 * Uses CSS `field-sizing: content` as a progressive enhancement (Chrome 123+)
 * so the height tracking happens natively without re-renders; the JS hook
 * remains as fallback for browsers that don't support it.
 */
export interface AutoGrowTextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "rows"> {
  /** Minimum visible rows (default 2). */
  minRows?: number;
  /** Optional max-height (px) — content scrolls inside past this. */
  maxHeight?: number;
  /** Внешний ref на сам <textarea> (React 19 ref-как-проп); сливается с внутренним. */
  ref?: Ref<HTMLTextAreaElement>;
}

export function AutoGrowTextarea({
  minRows = 2,
  maxHeight,
  value,
  onChange,
  style,
  ref: outerRef,
  ...rest
}: AutoGrowTextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  /* Внутренний ref нужен самому компоненту (resize); внешний доливаем рядом. */
  const setRefs = (node: HTMLTextAreaElement | null) => {
    ref.current = node;
    if (typeof outerRef === "function") outerRef(node);
    else if (outerRef) outerRef.current = node;
  };

  /* Compute new height from scrollHeight, clamped by maxHeight if provided. */
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    /* Reset first so scrollHeight reflects content height honestly (not the
       previously-set inline height). */
    el.style.height = "auto";
    let next = el.scrollHeight;
    if (maxHeight && next > maxHeight) next = maxHeight;
    el.style.height = `${next}px`;
    /* Toggle inner scrollbar visibility only when capped. */
    el.style.overflowY = maxHeight && el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [maxHeight]);

  /* Resize on mount + whenever `value` changes from outside. */
  useEffect(() => {
    resize();
  }, [resize, value]);

  /* Resize on font-load / window resize. */
  useEffect(() => {
    const onResize = () => resize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [resize]);

  return (
    <textarea
      ref={setRefs}
      rows={minRows}
      value={value}
      onChange={(e) => {
        onChange?.(e);
        /* Run after React commits the new value. */
        requestAnimationFrame(resize);
      }}
      style={{
        /* field-sizing handles native auto-grow in modern Chrome; JS keeps
           working for everything else. */
        fieldSizing: "content",
        ...style,
      } as React.CSSProperties}
      {...rest}
    />
  );
}
